import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Code } from 'lucide-react';
import { ttStatsAPI } from '../services/api';
import AnimatedText from '../components/AnimatedText';
import UnifiedFooter from '../components/UnifiedFooter';
import ClubPageSkeleton from '../components/ClubPageSkeleton';
import VenueCard from '../components/VenueCard';
import UnifiedClubWidget from '../components/widgets/UnifiedClubWidget';

interface Venue {
  id: string;
  name: string;
  address: string;
  phone?: string;
  website?: string;
  routePlanner?: string;
}

const ClubPage: React.FC = () => {
  const { id, lang } = useParams<{ id: string; lang?: string }>();
  const currentLang = (lang as 'en' | 'fr' | 'de' | 'it') || 'en';
  const navigate = useNavigate();
  const [clubData, setClubData] = useState<any>(null);
  const [teams, setTeams] = useState<any[]>([]);
  const [players, setPlayers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const translations = {
    en: {
      loadingClub: 'Loading club...',
      clubNotFound: 'Club not found',
      backToSearch: 'Back to search',
      teams: 'Teams',
      players: 'Players',
      matches: 'Matches',
      noTeamsFound: 'No teams found',
      noPlayersFound: 'No players found',
      noUpcomingMatches: 'No upcoming matches',
      captain: 'Captain',
      getDirections: 'Get directions',
      home: 'HOME',
      away: 'AWAY',
      vs: 'VS',
      upcoming: 'Upcoming',
      showMore: 'Show more',
      showLess: 'Show less'
    },
    fr: {
      loadingClub: 'Chargement du club...',
      clubNotFound: 'Club introuvable',
      backToSearch: 'Retour à la recherche',
      teams: 'Équipes',
      players: 'Joueurs',
      matches: 'Matchs',
      noTeamsFound: 'Aucune équipe trouvée',
      noPlayersFound: 'Aucun joueur trouvé',
      noUpcomingMatches: 'Aucun match à venir',
      captain: 'Capitaine',
      getDirections: 'Itinéraire',
      home: 'DOMICILE',
      away: 'EXTÉRIEUR',
      vs: 'VS',
      upcoming: 'À venir',
      showMore: 'Afficher plus',
      showLess: 'Afficher moins'
    },
    de: {
      loadingClub: 'Club wird geladen...',
      clubNotFound: 'Club nicht gefunden',
      backToSearch: 'Zurück zur Suche',
      teams: 'Teams',
      players: 'Spieler',
      matches: 'Spiele',
      noTeamsFound: 'Keine Teams gefunden',
      noPlayersFound: 'Keine Spieler gefunden',
      noUpcomingMatches: 'Keine kommenden Spiele',
      captain: 'Kapitän',
      getDirections: 'Route anzeigen',
      home: 'HEIM',
      away: 'AUSWÄRTS',
      vs: 'VS',
      upcoming: 'Kommende',
      showMore: 'Mehr anzeigen',
      showLess: 'Weniger anzeigen'
    },
    it: {
      loadingClub: 'Caricamento del club...',
      clubNotFound: 'Club non trovato',
      backToSearch: 'Torna alla ricerca',
      teams: 'Squadre',
      players: 'Giocatori',
      matches: 'Partite',
      noTeamsFound: 'Nessuna squadra trovata',
      noPlayersFound: 'Nessun giocatore trovato',
      noUpcomingMatches: 'Nessuna partita in programma',
      captain: 'Capitano',
      getDirections: 'Indicazioni',
      home: 'CASA',
      away: 'TRASFERTA',
      vs: 'VS',
      upcoming: 'Prossime',
      showMore: 'Mostra di più',
      showLess: 'Mostra meno'
    }
  };

  const t = translations[currentLang] || translations.en;

  useEffect(() => {
    if (!id) return;

    const fetchClubData = async () => {
      setLoading(true);
      try {
        console.log('Fetching club data for ID:', id);

        // Fetch all data in parallel
        const [clubInfo, clubTeams, clubPlayers] = await Promise.all([
          ttStatsAPI.getClubInfo(id),
          ttStatsAPI.getClubTeams(id),
          ttStatsAPI.getClubPlayers(id)
        ]);

        console.log('Club info received:', clubInfo);

        // Clean up club name by removing "Swiss Table Tennis" prefix
        if (clubInfo && clubInfo.name) {
          clubInfo.name = clubInfo.name.replace(/^Swiss Table Tennis\s+/i, '').trim();
        }

        setClubData(clubInfo);
        setTeams(clubTeams.teams || []);
        setPlayers(clubPlayers.players || []);
      } catch (error) {
        console.error('Failed to fetch club data:', error);
        console.error('Error details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClubData();
  }, [id]);

  if (loading) {
    return <ClubPageSkeleton />;
  }

  if (!clubData) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center transition-colors duration-300">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">Club not found</p>
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-2 mx-auto transition-colors duration-300"
          >
            <ArrowLeft className="h-4 w-4" />
            {t.backToSearch}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 -mt-16 transition-colors duration-300">
      {/* Header Section */}
      <div className="bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 pt-20 transition-colors duration-300">
        <div className="max-w-6xl mx-auto px-4 pb-6">
          {/* Club Name */}
          <AnimatedText
            text={clubData.name}
            className="text-3xl md:text-4xl font-light text-gray-900 dark:text-gray-100 mb-6 text-center transition-colors duration-300"
            delay={0.2}
            staggerDelay={0.02}
          />

          {/* Venue Cards with Individual Maps */}
          {clubData.venues && clubData.venues.length > 0 && (
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {clubData.venues.map((venue: Venue, index: number) => (
                <VenueCard
                  key={venue.id}
                  venue={venue}
                  index={index}
                  lang={currentLang}
                />
              ))}
            </div>
          )}

          {/* Club Widget */}
          <div className="mb-6">
            <UnifiedClubWidget
              clubId={id!}
              theme="light"
              lang={currentLang}
              defaultTab="matches"
              initialClubData={clubData}
              initialTeams={teams}
              initialPlayers={players}
            />
          </div>
        </div>
      </div>

      {/* Embed Widget CTA Section */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300"
        >
          <div className="text-left">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 transition-colors duration-300">
              {currentLang === 'en' && 'Embed this club on your website'}
              {currentLang === 'fr' && 'Intégrer ce club sur votre site web'}
              {currentLang === 'de' && 'Betten Sie diesen Club auf Ihrer Website ein'}
              {currentLang === 'it' && 'Incorpora questo club sul tuo sito web'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm transition-colors duration-300">
              {currentLang === 'en' && 'Display live matches, teams, and players on your club website with our free embeddable widgets.'}
              {currentLang === 'fr' && 'Affichez en direct matchs, équipes et joueurs sur le site de votre club avec nos widgets gratuits.'}
              {currentLang === 'de' && 'Zeigen Sie Live-Spiele, Teams und Spieler auf Ihrer Club-Website mit unseren kostenlosen Widgets.'}
              {currentLang === 'it' && 'Mostra partite, squadre e giocatori live sul sito del tuo club con i nostri widget gratuiti.'}
            </p>
            <Link
              to={`/${currentLang}/club/${id}/widget`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Code className="h-4 w-4" />
              {currentLang === 'en' && 'Configure Widget'}
              {currentLang === 'fr' && 'Configurer le widget'}
              {currentLang === 'de' && 'Widget konfigurieren'}
              {currentLang === 'it' && 'Configura widget'}
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <UnifiedFooter variant="showcase" lang={currentLang} />
    </div>
  );
};

export default ClubPage;